import React, { useState, useEffect, useCallback } from 'react';

const fmt = (n) => Number(n || 0).toFixed(2);

export default function Cash() {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [modal, setModal]       = useState(null);
  const [toast, setToast]       = useState('');
  const [canOpen, setCanOpen]   = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/cash/current', { headers: authHeaders() });
      const json = await r.json();
      setData(json);
    } catch {
      showToast('Błąd ładowania danych');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    fetch('/api/cash/can-open', { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setCanOpen(d.can_open === true))
      .catch(() => {});
  }, [load]);

  async function handleOpen(openingFloat) {
    try {
      const r = await fetch('/api/cash/open', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ opening_float: Number(openingFloat) || 0 }),
      });
      const result = await r.json();
      if (!result.ok) throw new Error(result.error || 'Błąd otwarcia raportu');
      showToast('✓ Raport kasowy otwarty');
      setModal(null);
      load();
    } catch (err) { showToast(err.message); }
  }

  async function handleClose(fiscalReportType) {
    if (!data?.report) return;
    try {
      const r = await fetch(`/api/cash/${data.report.id}/close`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const result = await r.json();
      if (!result.ok) throw new Error(result.error || 'Błąd zamknięcia raportu');
      showToast('✓ Raport kasowy zamknięty');
      setModal(null);
      load();

      // Opcjonalny wydruk raportu dobowego X/Z z drukarki fiskalnej - nie blokuje
      // zamknięcia raportu kasowego, nawet jeśli się nie powiedzie
      if (fiscalReportType) {
        try {
          const fr = await fetch(`/api/fiscal/report/${fiscalReportType}`, {
            method: 'POST', headers: authHeaders(),
          });
          const fresult = await fr.json();
          if (!fresult.ok) throw new Error(fresult.error || 'Błąd druku');
          showToast(`✓ Raport dobowy ${fiscalReportType.toUpperCase()} wydrukowany`);
        } catch (err) {
          showToast(`⚠ Raport kasowy zamknięty, ale raport dobowy nie wydrukował się: ${err.message}`);
        }
      }
    } catch (err) { showToast(err.message); }
  }

  async function handleOperation(type, amount, description) {
    try {
      const r = await fetch('/api/cash/operation', {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, amount: Number(amount), description }),
      });
      const result = await r.json();
      if (!result.ok) throw new Error(result.error || 'Błąd operacji');
      showToast(type === 'in' ? `✓ Wpłacono ${fmt(amount)} zł` : `✓ Wypłacono ${fmt(amount)} zł`);
      setModal(null);
      load();
    } catch (err) { showToast(err.message); }
  }

  if (loading) return <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Ładowanie...</div>;

  // Brak otwartego raportu — ekran startowy
  if (!data) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px 16px' }}>
        {toast && <Toast msg={toast} />}
        <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>💰 Kasa</h2>
        <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 32, textAlign: 'center', border: '0.5px solid var(--border)' }}>
          <div style={{ fontSize: 15, color: 'var(--text-muted)', marginBottom: 16 }}>Raport kasowy nie jest jeszcze otwarty</div>
          {canOpen ? (
            <button onClick={() => setModal('open')} style={{ padding: '13px 28px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600 }}>
              Otwórz raport kasowy
            </button>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Poczekaj na otwarcie raportu przez managera lub admina</div>
          )}
        </div>
        {modal === 'open' && <OpenReportModal onConfirm={handleOpen} onClose={() => setModal(null)} />}
      </div>
    );
  }

  const { report, cash, card, blik, transfer, receipt, total_sales, order_count, operations } = data;
  const isOpen = report.status === 'open';
  const canClose = data.can_close === true;

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '20px 16px' }}>
      {toast && <Toast msg={toast} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600 }}>💰 Kasa</h2>
        <span style={{
          fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 500,
          background: isOpen ? '#f0fdf4' : '#f1f5f9',
          color: isOpen ? '#16a34a' : 'var(--text-muted)',
        }}>
          {isOpen ? 'Otwarty' : 'Zamknięty'}
        </span>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
        Otwarty: {new Date(report.opened_at).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })}
        {report.closed_at && ` · Zamknięty: ${new Date(report.closed_at).toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })}`}
      </div>

      {/* Stan kasy — gotówka w szufladzie */}
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 20, marginBottom: 16, border: '0.5px solid var(--border)' }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 12 }}>Gotówka w szufladzie</div>
        <div style={{ fontSize: 42, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>
          {fmt(cash.in_drawer)} <span style={{ fontSize: 22, fontWeight: 500, color: 'var(--text-muted)' }}>zł</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
          <StatBox label="Wkład" value={fmt(cash.opening_float)} color="var(--text-muted)" />
          <StatBox label="Sprzedaż" value={fmt(cash.sales)} color="#16a34a" />
          <StatBox label="Wpłaty" value={fmt(cash.in)} color="var(--blue)" />
          <StatBox label="Wypłaty" value={fmt(cash.out)} color="#dc2626" />
        </div>
      </div>

      {/* Sprzedaż wg metody */}
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 20, marginBottom: 16, border: '0.5px solid var(--border)' }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 12 }}>
          Sprzedaż w tym raporcie — {order_count || 0} zamówień
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
          <PayBox icon="💵" label="Gotówka" value={fmt(cash.sales)} />
          <PayBox icon="💳" label="Karta" value={fmt(card.sales)} />
          <PayBox icon="📱" label="BLIK" value={fmt(blik.sales)} />
          <PayBox icon="🏦" label="Przelew" value={fmt(transfer.sales)} />
          <PayBox icon="🧾" label="Paragon" value={fmt(receipt.sales)} />
        </div>
        <div style={{ borderTop: '0.5px solid var(--border)', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>RAZEM</span>
          <span style={{ fontSize: 20, fontWeight: 700 }}>{fmt(total_sales)} zł</span>
        </div>
      </div>

      {isOpen && (
        <>
          {/* Przyciski wpłata/wypłata */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <button onClick={() => setModal('in')} style={{
              padding: '14px', border: 'none', borderRadius: 10, cursor: 'pointer',
              background: '#f0fdf4', color: '#16a34a', fontSize: 15, fontWeight: 600,
            }}>
              ↓ Wpłata do kasy
            </button>
            <button onClick={() => setModal('out')} style={{
              padding: '14px', border: 'none', borderRadius: 10, cursor: 'pointer',
              background: '#fef2f2', color: '#dc2626', fontSize: 15, fontWeight: 600,
            }}>
              ↑ Wypłata z kasy
            </button>
          </div>

          {canClose && <button onClick={() => setModal('close')} style={{
            width: '100%', padding: '14px', background: 'var(--blue)', color: '#fff',
            border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600, marginBottom: 20,
          }}>
            Zamknij raport kasowy
          </button>}
        </>
      )}

      {/* Historia operacji */}
      {operations.length > 0 && (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '0.5px solid var(--border)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', borderBottom: '0.5px solid var(--border)' }}>
            Operacje w tym raporcie
          </div>
          {operations.map(op => (
            <div key={op.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '0.5px solid var(--border)', gap: 12 }}>
              <div style={{ fontSize: 20 }}>{op.type === 'in' ? '↓' : '↑'}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{op.description || (op.type === 'in' ? 'Wpłata' : 'Wypłata')}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {new Date(op.created_at).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                  {op.added_by && <span> · {op.added_by}</span>}
                </div>
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: op.type === 'in' ? '#16a34a' : '#dc2626' }}>
                {op.type === 'in' ? '+' : '-'}{fmt(op.amount)} zł
              </div>
            </div>
          ))}
        </div>
      )}

      {(modal === 'in' || modal === 'out') && (
        <OperationModal type={modal} onConfirm={handleOperation} onClose={() => setModal(null)} />
      )}
      {modal === 'close' && (
        <CloseReportModal cashInDrawer={cash.in_drawer} onConfirm={handleClose} onClose={() => setModal(null)} />
      )}
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 8px' }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color }}>{value} zł</div>
    </div>
  );
}

function PayBox({ icon, label, value }) {
  return (
    <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{icon} {label}</span>
      <span style={{ fontSize: 15, fontWeight: 600 }}>{value} zł</span>
    </div>
  );
}

function OpenReportModal({ onConfirm, onClose }) {
  const [amount, setAmount] = useState('');

  const handleKey = (key) => {
    if (key === 'C') { setAmount(''); return; }
    if (key === '⌫') { setAmount(p => p.slice(0, -1)); return; }
    if (key === '.' && amount.includes('.')) return;
    if (amount === '0' && key !== '.') { setAmount(key); return; }
    if (amount.length >= 8) return;
    setAmount(p => p + key);
  };

  const KEYS = [['7','8','9'],['4','5','6'],['1','2','3'],['C','0','.']];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 340, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Otwórz raport kasowy</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>Kwota początkowa w szufladzie (wkład własny)</div>

        <div style={{ background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 700 }}>
            {amount || '0'} <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-muted)' }}>zł</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
          {KEYS.flat().map(key => (
            <button key={key} onClick={() => handleKey(key)} style={{
              padding: '15px 0', fontSize: key === 'C' ? 14 : 20, fontWeight: 600,
              border: '0.5px solid var(--border)', borderRadius: 8, cursor: 'pointer',
              background: key === 'C' ? '#fef2f2' : 'var(--bg)',
              color: key === 'C' ? '#dc2626' : 'var(--text)',
            }}>{key}</button>
          ))}
          <button onClick={() => handleKey('⌫')} style={{
            padding: '15px 0', fontSize: 20, border: '0.5px solid var(--border)',
            borderRadius: 8, cursor: 'pointer', background: 'var(--bg)', color: 'var(--text-muted)',
          }}>⌫</button>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '13px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>
            Anuluj
          </button>
          <button onClick={() => onConfirm(amount)} style={{
            flex: 2, padding: '13px', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
            background: 'var(--blue)', color: '#fff', cursor: 'pointer',
          }}>
            Otwórz raport
          </button>
        </div>
      </div>
    </div>
  );
}

function CloseReportModal({ cashInDrawer, onConfirm, onClose }) {
  const [confirmed, setConfirmed] = useState(false);
  const [printFiscal, setPrintFiscal] = useState(true);
  const [fiscalType, setFiscalType] = useState('x'); // domyślnie X (czytający, bezpieczny) - Z trzeba świadomie wybrać

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 340, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>Zamknij raport kasowy</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ background: 'var(--bg)', borderRadius: 8, padding: '14px', marginBottom: 16, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Powinno być w szufladzie</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>{fmt(cashInDrawer)} zł</div>
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
          Przeliczyłem gotówkę w szufladzie i zgadza się z wyliczoną kwotą
        </label>

        <div style={{ border: '0.5px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            <input type="checkbox" checked={printFiscal} onChange={e => setPrintFiscal(e.target.checked)} />
            Wydrukuj raport dobowy z drukarki fiskalnej
          </label>
          {printFiscal && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, paddingLeft: 26 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                <input type="radio" name="fiscalType" checked={fiscalType === 'x'} onChange={() => setFiscalType('x')} />
                X - czytający
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                <input type="radio" name="fiscalType" checked={fiscalType === 'z'} onChange={() => setFiscalType('z')} />
                Z - zerujący
              </label>
            </div>
          )}
          {printFiscal && fiscalType === 'z' && (
            <div style={{ fontSize: 11, color: 'var(--red, #A32D2D)', marginTop: 8, paddingLeft: 26 }}>
              ⚠ Zeruje liczniki fiskalne - użyj tylko przy ostatnim zamknięciu w tym dniu!
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '13px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>
            Anuluj
          </button>
          <button onClick={() => onConfirm(printFiscal ? fiscalType : null)} disabled={!confirmed} style={{
            flex: 2, padding: '13px', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
            background: confirmed ? 'var(--blue)' : 'var(--border)',
            color: confirmed ? '#fff' : 'var(--text-muted)',
            cursor: confirmed ? 'pointer' : 'not-allowed',
          }}>
            Zamknij raport
          </button>
        </div>
      </div>
    </div>
  );
}

function OperationModal({ type, onConfirm, onClose }) {
  const [amount, setAmount] = useState('');
  const [desc, setDesc]     = useState('');
  const isIn = type === 'in';

  const handleKey = (key) => {
    if (key === 'C') { setAmount(''); return; }
    if (key === '⌫') { setAmount(p => p.slice(0, -1)); return; }
    if (key === '.' && amount.includes('.')) return;
    if (amount === '0' && key !== '.') { setAmount(key); return; }
    if (amount.length >= 8) return;
    setAmount(p => p + key);
  };

  const KEYS = [['7','8','9'],['4','5','6'],['1','2','3'],['C','0','.']];
  const valid = parseFloat(amount) > 0;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 340, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{isIn ? '↓ Wpłata do kasy' : '↑ Wypłata z kasy'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>

        <div style={{ background: 'var(--bg)', border: '1.5px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, textAlign: 'right' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>Kwota</div>
          <div style={{ fontSize: 28, fontWeight: 700 }}>
            {amount || '0'} <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-muted)' }}>zł</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
          {KEYS.flat().map(key => (
            <button key={key} onClick={() => handleKey(key)} style={{
              padding: '15px 0', fontSize: key === 'C' ? 14 : 20, fontWeight: 600,
              border: '0.5px solid var(--border)', borderRadius: 8, cursor: 'pointer',
              background: key === 'C' ? '#fef2f2' : 'var(--bg)',
              color: key === 'C' ? '#dc2626' : 'var(--text)',
            }}>{key}</button>
          ))}
          <button onClick={() => handleKey('⌫')} style={{
            padding: '15px 0', fontSize: 20, border: '0.5px solid var(--border)',
            borderRadius: 8, cursor: 'pointer', background: 'var(--bg)', color: 'var(--text-muted)',
          }}>⌫</button>
        </div>

        <input
          value={desc}
          onChange={e => setDesc(e.target.value)}
          placeholder={isIn ? 'Opis (np. otwarcie kasy)' : 'Opis (np. zakup towarów)'}
          style={{ width: '100%', padding: '10px 12px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', fontSize: 14, marginBottom: 12, boxSizing: 'border-box' }}
        />

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '13px', border: '0.5px solid var(--border)', borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 14, color: 'var(--text-muted)' }}>
            Anuluj
          </button>
          <button onClick={() => valid && onConfirm(type, amount, desc)} disabled={!valid} style={{
            flex: 2, padding: '13px', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600,
            background: valid ? (isIn ? '#16a34a' : '#dc2626') : 'var(--border)',
            color: valid ? '#fff' : 'var(--text-muted)', cursor: valid ? 'pointer' : 'not-allowed',
          }}>
            {isIn ? 'Wpłać' : 'Wypłać'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Toast({ msg }) {
  return (
    <div style={{ position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', background: '#1e293b', color: '#fff', padding: '10px 20px', borderRadius: 8, zIndex: 999, fontSize: 14 }}>
      {msg}
    </div>
  );
}

function authHeaders() {
  const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
  return { Authorization: `Bearer ${token}` };
}
